/**
 * Profile Questionnaire validation.
 *
 * Pure field-level validators. The reducer in `update.ts` calls these
 * to compute per-field errors and to derive `isFormValid`. Backend
 * remains authoritative — these helpers exist for responsiveness and
 * usability only.
 *
 * Length limits mirror backend (`internal/profiles/service.go`
 * `MaxDisplayName`/`MaxHeadline`/`MaxBio`/`MaxCityOrRegion`) via
 * `constants.ts`.
 */

import {
  MAX_DISPLAY_NAME,
  MAX_HEADLINE,
  MAX_BIO,
  MIN_AGE_LOCAL,
  MAX_AGE_LOCAL,
  CITY_MVP,
} from './constants';

export type ValidationErrorCode =
  | 'DisplayNameRequired'
  | 'DisplayNameTooLong'
  | 'GenderRequired'
  | 'ScenarioRequired'
  | 'AgeRequired'
  | 'AgeTooYoung'
  | 'AgeTooOld'
  | 'CityRequired'
  | 'CityNotInMvp'
  | 'HeadlineTooLong'
  | 'BioTooLong'
  | 'HeadlineOrBioRequired';

export interface FieldValidationError {
  readonly field: string;
  readonly code: ValidationErrorCode;
  readonly message: string;
}

export interface NameValidation {
  readonly valid: boolean;
  readonly errors: readonly FieldValidationError[];
}
export interface AgeValidation {
  readonly valid: boolean;
  readonly errors: readonly FieldValidationError[];
}
export interface CityValidation {
  readonly valid: boolean;
  readonly errors: readonly FieldValidationError[];
}
export interface GenderValidation {
  readonly valid: boolean;
  readonly errors: readonly FieldValidationError[];
}
export interface ScenarioValidation {
  readonly valid: boolean;
  readonly errors: readonly FieldValidationError[];
}
export interface HeadlineValidation {
  readonly valid: boolean;
  readonly errors: readonly FieldValidationError[];
}
export interface BioValidation {
  readonly valid: boolean;
  readonly errors: readonly FieldValidationError[];
}

/**
 * Validate `display_name`. Required for activation per PC-013 / REQ-125.
 */
export function validateName(value: string | undefined): NameValidation {
  const errors: FieldValidationError[] = [];
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) {
    errors.push({
      field: 'display_name',
      code: 'DisplayNameRequired',
      message: 'Имя обязательно',
    });
  } else if ([...trimmed].length > MAX_DISPLAY_NAME) {
    errors.push({
      field: 'display_name',
      code: 'DisplayNameTooLong',
      message: `Имя должно быть не длиннее ${MAX_DISPLAY_NAME} символов`,
    });
  }
  return { valid: errors.length === 0, errors };
}

/** Validate gender (self-gender, per CONTRACT-002.1). */
export function validateGender(value: 'male' | 'female' | undefined): GenderValidation {
  const errors: FieldValidationError[] = [];
  if (value !== 'male' && value !== 'female') {
    errors.push({
      field: 'gender',
      code: 'GenderRequired',
      message: 'Укажите пол',
    });
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Validate the selected scenario. The questionnaire selects one of
 * `equal` / `sponsor` / `no_work`; the backend enum translation lives
 * in `rolePresentation.ts`.
 */
export function validateScenario(value: string | undefined): ScenarioValidation {
  const errors: FieldValidationError[] = [];
  if (value !== 'equal' && value !== 'sponsor' && value !== 'no_work') {
    errors.push({
      field: 'scenario',
      code: 'ScenarioRequired',
      message: 'Выберите сценарий',
    });
  }
  return { valid: errors.length === 0, errors };
}

/** Validate age 18..120 per REQ-125. */
export function validateAge(value: number | undefined): AgeValidation {
  const errors: FieldValidationError[] = [];
  if (value === undefined || !Number.isFinite(value)) {
    errors.push({
      field: 'age',
      code: 'AgeRequired',
      message: 'Укажите возраст',
    });
  } else if (value < MIN_AGE_LOCAL) {
    errors.push({
      field: 'age',
      code: 'AgeTooYoung',
      message: `Возраст должен быть не меньше ${MIN_AGE_LOCAL}`,
    });
  } else if (value > MAX_AGE_LOCAL) {
    errors.push({
      field: 'age',
      code: 'AgeTooOld',
      message: `Возраст должен быть не больше ${MAX_AGE_LOCAL}`,
    });
  }
  return { valid: errors.length === 0, errors };
}

/** Validate city: required (per REQ-125) AND constrained to MVP enum (PC-015). */
export function validateCity(value: string | undefined): CityValidation {
  const errors: FieldValidationError[] = [];
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) {
    errors.push({
      field: 'city',
      code: 'CityRequired',
      message: 'Город обязателен',
    });
  } else if (!CITY_MVP.includes(trimmed)) {
    errors.push({
      field: 'city',
      code: 'CityNotInMvp',
      message: `Город должен быть одним из: ${CITY_MVP.join(', ')}`,
    });
  }
  return { valid: errors.length === 0, errors };
}

/** Validate headline (length only; requiredness is OR'd with bio). */
export function validateHeadline(value: string | undefined): HeadlineValidation {
  const errors: FieldValidationError[] = [];
  const v = value ?? '';
  if ([...v].length > MAX_HEADLINE) {
    errors.push({
      field: 'headline',
      code: 'HeadlineTooLong',
      message: `Заголовок должен быть не длиннее ${MAX_HEADLINE} символов`,
    });
  }
  return { valid: errors.length === 0, errors };
}

/** Validate bio (length only). */
export function validateBio(value: string | undefined): BioValidation {
  const errors: FieldValidationError[] = [];
  const v = value ?? '';
  if ([...v].length > MAX_BIO) {
    errors.push({
      field: 'bio',
      code: 'BioTooLong',
      message: `Описание должно быть не длиннее ${MAX_BIO} символов`,
    });
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Compose all field validations into a flat list. Used by the reducer.
 */
export function validateAllFields(input: {
  display_name?: string;
  gender?: 'male' | 'female';
  scenario?: string;
  age?: number;
  city?: string;
  headline?: string;
  bio?: string;
}): readonly FieldValidationError[] {
  const errors: FieldValidationError[] = [];
  errors.push(...validateName(input.display_name).errors);
  errors.push(...validateGender(input.gender).errors);
  errors.push(...validateScenario(input.scenario).errors);
  errors.push(...validateAge(input.age).errors);
  errors.push(...validateCity(input.city).errors);
  errors.push(...validateHeadline(input.headline).errors);
  errors.push(...validateBio(input.bio).errors);

  // headline OR bio (per REQ-125). Only checked when both fields are
  // present-and-empty.
  const headlineEmpty = !(input.headline ?? '').trim();
  const bioEmpty = !(input.bio ?? '').trim();
  if (headlineEmpty && bioEmpty) {
    errors.push({
      field: 'headline_or_bio',
      code: 'HeadlineOrBioRequired',
      message: 'Заполните заголовок или описание',
    });
  }

  return errors;
}

/** Filter errors to those for a single field. */
export function errorsForField(
  errors: readonly FieldValidationError[],
  field: string
): readonly FieldValidationError[] {
  return errors.filter((e) => e.field === field);
}